import { isAxiosError } from "axios";
import { axiosInstance } from "contexts/axiosApiInstance";
import { createLogger } from "debug/DebugingLogger";

const log = createLogger("OrganizationsApi");

/// Mirrors the server `OrgResponse` (organizations/management.rs).
export interface Org {
  org_id: string;
  name: string;
  slug: string;
  /// The auto-minted single-member org every account has. Cannot be deleted
  /// (that is account deletion) and cannot take members (D14). Name and
  /// image ARE editable — presentation, not structure.
  is_personal: boolean;
  created_at: string;
  /// Whether a custom image is set; the bytes live behind
  /// `/organizations/{id}/image`, membership-gated.
  has_image: boolean;
  /// The caller's strongest role: "owner" | "org_admin" | "member". Governor
  /// surfaces (the quota widget) gate on owner/org_admin. Absent if the
  /// server's role read failed.
  role?: string;
  /// ISO timestamp; present only while the org sits in its deletion grace
  /// window. The org is read-only and any admin can still cancel.
  deletion_scheduled_at?: string;
  /// Set once the cascade has claimed it — past this there is nothing to cancel.
  deletion_started_at?: string;
  /// When the window closes — server-owned, because a personal org runs on its
  /// account's 48 hours while a non-personal org runs 30 days (D55).
  purge_at?: string;
  /// The open period's plan code, and its label as the catalog spells it
  /// today. Both absent when the org has no recorded period — the switcher
  /// then badges nothing rather than claiming a tier.
  plan_code?: string;
  plan_name?: string;
  /// The features the caller may use here, and those shown as coming soon
  /// (docs/FeatureReleases.md). An app kind's feature is named for the kind.
  features?: string[];
  coming_soon?: string[];
}

/// Mirrors the server `OrgDeletionScheduledResponse`.
export interface OrgDeletionScheduled {
  scheduled_at: string;
  purge_at: string;
  spaces_scheduled: number;
}

/// Mirrors the server `SpaceResponse` (organizations/spaces.rs).
export interface Space {
  space_id: string;
  org_id: string;
  parent_space_id: string | null;
  name: string;
  slug: string;
  /// Legacy flag, retiring with D25 — presentation must not fork on it.
  is_personal: boolean;
  /// Palette short-name (`"sky"`), empty = derive from the id.
  color: string;
  /// `open` | `restricted` | `sealed` (D39/D50). Open nests and inherits; Restricted
  /// and Sealed are top-level and advertised to nobody, Sealed also walled.
  classification: string;
  /// `regular`, or `app` for an app's own space, which the working surfaces
  /// hide (docs/Apps.md). Absent on a server that predates it.
  kind?: string;
  /// Derived from `classification` — renders the lock.
  sealed: boolean;
  /// Reached through `org_governor` alone: metadata, never access (D3/D13).
  /// The working surfaces hide these; org settings ▸ Spaces is where they
  /// belong. Always false for anyone who is not a governor.
  governed_only: boolean;
  /// ISO timestamp; present only while the space sits in its deletion grace
  /// window. Read-only until then, and cancellable by a manager or governor.
  deletion_scheduled_at?: string;
  /// Set once the cascade has claimed it — past this there is nothing to cancel.
  deletion_started_at?: string;
  /// Effective managers — direct plus whatever an Open child inherits from its
  /// parent. Served on the org tree to governors only, so ABSENT means "not
  /// asked / not knowable", never zero. Read it through `isAbandoned`.
  manager_count?: number;
}

/// A space nobody manages any more — the state an account deletion leaves
/// behind. Undefined `manager_count` is not an answer, so it is not a flag.
export function isAbandoned(space: Space): boolean {
  return space.manager_count === 0;
}

/// A space in the Trash in its own right: a child goes, and comes back, with
/// its parent (D53), so only the roots are listed and counted.
export function isTrashedRoot(
  space: Space,
  byId: ReadonlyMap<string, Space>,
): boolean {
  return (
    !!space.deletion_scheduled_at &&
    !byId.get(space.parent_space_id ?? "")?.deletion_scheduled_at
  );
}

/// Someone who already holds a grant on a space and could take it over.
export interface ManagerCandidate {
  client_id: string;
  email: string;
  name: string;
  /// The grant they hold today — `collaborator` or `viewer`.
  role: string;
  /// Held on an ancestor space rather than on this one.
  inherited: boolean;
}

export interface OrgMember {
  client_id: string;
  email: string;
  display_name: string;
  role: string;
  joined_at: string | null;
  /// Invited but not yet registered — renders the Invited badge.
  pending: boolean;
}

/// One row of a space's access list. `is_org_member === false` renders the
/// guest chip (D26).
export interface SpaceGrant {
  subject: string;
  role: string;
  email: string | null;
  name: string | null;
  is_org_member: boolean | null;
}

export interface GovernanceAuditEntry {
  id: number;
  org_id: string;
  space_id: string | null;
  /// The raw recorded identity. `actor` is a bare client id; `subject` may be
  /// an FGA subject (`user:cli-…`) for space grants.
  actor: string;
  actor_name: string | null;
  actor_email: string | null;
  action: string;
  subject: string | null;
  subject_name: string | null;
  subject_email: string | null;
  detail: Record<string, unknown> | null;
  created_at: string;
}

/// Mirrors the server `PlanLimits` — `null` means unlimited, not zero.
export interface PlanLimits {
  storage_bytes: number | null;
  max_models: number | null;
  max_tables: number | null;
  mcp_queries_per_month: number | null;
  egress_bytes_per_month: number | null;
  seats: number | null;
}

/// One metered resource (`MeteredUsage` in plans.rs). `limit: null` is
/// UNLIMITED, not zero — rendering it as 0 draws a full bar over an unbounded
/// allowance, the exact inversion of the truth.
export interface MeteredUsage {
  used: number;
  limit: number | null;
  /// Unix seconds: the org's next billing anchor day, when both
  /// counters rotate.
  resets_at: number;
  /// What an admin added for this period; already inside `limit`.
  extra?: number;
}

/// An allowance given once (`LifetimeUsage` in plans.rs): nothing resets it.
export interface LifetimeUsage {
  used: number;
  limit: number;
}

/// `read_only_on` / `revoke_on` are unix seconds; `day` counts from the first
/// failed charge.
export type PaymentStanding =
  | { state: "current" }
  | { state: "grace"; day: number; read_only_on: number }
  | { state: "read_only"; day: number; revoke_on: number };

/// Every metered allowance an org holds, storage included (from the key the gate
/// compares against); storage's `resets_at` is never rendered.
export interface OrgUsage {
  egress: MeteredUsage;
  mcp_calls: MeteredUsage;
  /// A personal account's one-time trial on Mycorr's model, in cents; absent
  /// on an organization, which runs on its own key.
  agent_trial?: LifetimeUsage;
  /// Web lookups the apps made, ever; an organization has none yet.
  searches: LifetimeUsage;
  storage: MeteredUsage;
  standing: PaymentStanding;
  /// Only when asked for, and only for someone who may see the members.
  mcp_breakdown?: McpBreakdown;
}

/// One caller's UTC day. `failed` is part of `charged`; `refused` never was.
export interface McpDayUsage {
  /// `YYYY-MM-DD`, UTC.
  day: string;
  client_id: string;
  charged: number;
  refused: number;
  failed: number;
}

/// Neither name nor email is a former member.
export interface McpCaller {
  client_id: string;
  name: string | null;
  /// A current member's address; absent for anyone else.
  email: string | null;
}

/// The breakdown keeps the counter's lifetime: this period and the one before.
export type McpPeriod = "current" | "previous";

/// Who spent a period's MCP calls. The days add up to `used`;
/// `before_tracking` is what the counter held before days were recorded.
export interface McpBreakdown {
  /// Unix seconds, midnight UTC; the end is exclusive.
  period_start: number;
  period_end: number;
  used: number;
  before_tracking: number;
  days: McpDayUsage[];
  members: McpCaller[];
}

/// The allowances actually run out, storage first — it is the only one
/// waiting does not fix. No ceiling is never spent; a ceiling of zero is spent
/// at zero used.
export function spentLimits(usage: OrgUsage | null): MeteredLimit[] {
  if (!usage) return [];
  const rows: [MeteredLimit, MeteredUsage][] = [
    ["storage", usage.storage],
    ["egress", usage.egress],
    ["mcp_calls", usage.mcp_calls],
  ];
  return rows
    .filter(([, meter]) => meter.limit !== null && meter.used >= meter.limit)
    .map(([name]) => name);
}

export type MeteredLimit = "storage" | "egress" | "mcp_calls";

/// How full an allowance is, or `null` when nothing caps it. A bar needs a
/// denominator, and drawing `null` as zero would paint a full bar over an
/// unbounded allowance.
export function meterPercent(meter: MeteredUsage): number | null {
  if (meter.limit === null) return null;
  if (meter.limit <= 0) return 100;
  return Math.min(100, (meter.used / meter.limit) * 100);
}

/// One row of the public catalog (`CatalogPlanResponse`) — the CURRENT offer,
/// which is what a pricing surface shows. Never the answer to "what does this
/// org have"; that is `fetchCurrentPlan`.
export interface CatalogPlan {
  code: string;
  name: string;
  description: string | null;
  price_cents: number;
  /// `null` for a tier not sold by the year.
  price_cents_yearly: number | null;
  currency: string;
  billing_period: string;
  limits: PlanLimits;
  sort_order: number;
  /// False for a tier seeded ahead of its launch, so a surface can say "coming
  /// soon" without hardcoding which tier that is.
  is_purchasable: boolean;
}

/// An org's current plan (`CurrentPlanResponse` in plans.rs): the period's
/// snapshot, except the display name, which follows the catalog.
export interface CurrentPlan {
  code: string;
  name: string;
  description: string | null;
  price_cents: number;
  currency: string;
  billing_period: string;
  limits: PlanLimits;
  started_at: string;
  /// The paid plan this org came down from; present only while on a free one.
  previous?: {
    name: string;
    ended_at: string;
    /// `ended`: the subscription is over. `changed`: an override or downgrade.
    reason: "ended" | "changed";
  };
}

/// The plan of one org the caller belongs to (none: their personal org). A 404 is
/// an answer, an org with no recorded period, not a label to invent.
export async function fetchCurrentPlan(
  orgId?: string,
): Promise<CurrentPlan | null> {
  const plan = await readCurrentPlan(orgId);
  return plan ?? null;
}

/// `null` is "no period recorded" (404), `undefined` a read that failed — a
/// failed read must not render as "no plan" and offer Upgrade to a paying org.
export async function readCurrentPlan(
  orgId?: string,
): Promise<CurrentPlan | null | undefined> {
  try {
    const response = await axiosInstance.get<CurrentPlan>(
      "/action/account/plan",
      { params: orgId ? { org_id: orgId } : undefined },
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    log.error("fetchCurrentPlan failed", error);
    return undefined;
  }
}

/// Usage for one org the caller belongs to; `null` on failure, so the panel still
/// shows the plan. `mcpBreakdown` only from Plan & usage: every open tab polls
/// this, and the breakdown is the costly part.
export async function fetchOrgUsage(
  orgId?: string,
  { mcpBreakdown }: { mcpBreakdown?: McpPeriod } = {},
): Promise<OrgUsage | null> {
  try {
    const response = await axiosInstance.get<OrgUsage>(
      "/action/account/usage",
      {
        params: {
          ...(orgId ? { org_id: orgId } : {}),
          ...(mcpBreakdown ? { mcp_breakdown: mcpBreakdown } : {}),
        },
      },
    );
    return response.data;
  } catch (error) {
    log.error("fetchOrgUsage failed", error);
    return null;
  }
}

interface ScheduledChangeView {
  action: string;
  /// Unix seconds.
  effective_at: number | null;
  resume_at: number | null;
}

export interface SubscriptionView {
  status: string;
  /// `null` without `can_manage_billing`.
  subscription_id: string | null;
  /// Seats bought, not the org's member count.
  seats: number;
  current_period_start: number | null;
  current_period_end: number | null;
  past_due_since: number | null;
  scheduled_change: ScheduledChangeView | null;
  double_billed: boolean;
  /// A seat reduction parked for the period boundary; `null` when none is.
  pending_seats: PendingSeatsView | null;
  currency_code: string | null;
  /// Per seat per billing period, lowest unit; `null` when unknown.
  unit_price_minor: number | null;
  /// Paddle adds tax on top of that price; `null` before the first charge.
  tax_on_top: boolean | null;
}

export interface PendingSeatsView {
  seats: number;
  /// Unix seconds.
  effective_at: number | null;
}

export interface BillingContext {
  /// Advisory; every billing door re-checks it.
  can_manage_billing: boolean;
  /// `null` for an org never billed.
  processor_customer_id: string | null;
  /// `null` for an org never billed and for one whose subscription is cancelled.
  subscription: SubscriptionView | null;
  /// Deliveries landed but not yet applied. Non-zero with `subscription` still
  /// `null` is the one window Subscribe must not be offered.
  pending_events: number;
  /// Deliveries that failed too many times: no longer "applying", not applied
  /// either. Subscribe stays withheld — the stuck delivery may BE the payment.
  stuck_events: number;
  /// A cancelled org can lose its billing account row and keep every invoice.
  has_billing_history: boolean;
  /// Owners only.
  billed_to: string | null;
  /// Answered by the same predicate the portal door enforces.
  can_open_portal: boolean;
  /// Invoices still go to a payer who has left; cleared by a card change or a
  /// dismiss.
  payer_left: boolean;
  /// `null` when unasked or unknown — never a false "no card".
  card_on_file: boolean | null;
  /// `null` until a confirmed payment has named one.
  paid_by: { name: string; email: string; is_you: boolean } | null;
  /// The card behind the last charge; `null` when there is none or Paddle
  /// described none — `card_on_file` stays the answer.
  card: {
    brand: string | null;
    last4: string | null;
    expires: string | null;
  } | null;
}

interface CheckoutPreflight {
  may_check_out: boolean;
  /// `subscribed` · `restored` · `unattached` · `unreachable`.
  reason: string | null;
  plan_code: string | null;
  /// Rides to the processor in `custom_data`; the server spends it on the first
  /// delivery. Present iff `may_check_out`.
  checkout_token: string | null;
  /// Another billing account holds the buyer's own address, so this org must
  /// be billed at an address of its own.
  needs_billing_email?: boolean;
}

/// A network failure answers a refusal: nothing opens checkout on a maybe.
export async function preflightCheckout(
  orgId: string | undefined,
): Promise<CheckoutPreflight> {
  try {
    const response = await axiosInstance.post<CheckoutPreflight>(
      "/action/account/billing/checkout-preflight",
      null,
      { params: orgId ? { org_id: orgId } : undefined },
    );
    return response.data;
  } catch (error) {
    log.error("checkout pre-flight failed", error);
    return {
      may_check_out: false,
      reason: "unreachable",
      plan_code: null,
      checkout_token: null,
      needs_billing_email: false,
    };
  }
}

export interface UnattachedSubscription {
  subscription_id: string;
  status: string;
  seats: number;
  plan_name: string | null;
  /// What the checkout said it was for; the one rule the server enforces on
  /// attach.
  org_kind: string | null;
  org_name: string | null;
  /// Unix seconds.
  since: number | null;
  attachable: boolean;
}

/// `[]` on any failure.
export async function fetchUnattachedSubscriptions(
  orgId: string | undefined,
): Promise<UnattachedSubscription[]> {
  try {
    const response = await axiosInstance.get<{
      subscriptions: UnattachedSubscription[];
    }>("/action/account/billing/unattached", {
      params: orgId ? { org_id: orgId } : undefined,
    });
    return response.data.subscriptions;
  } catch (error) {
    log.error("fetchUnattachedSubscriptions failed", error);
    return [];
  }
}

/// `over_seats` is the door's own comparison of the roster against the
/// subscription: attaching under it is legal and keeps everyone, but no one new
/// can be invited until seats are added.
export async function attachSubscription(
  orgId: string | undefined,
  subscriptionId: string,
): Promise<{
  plan_code: string | null;
  seats: number;
  members: number;
  over_seats: boolean;
} | null> {
  try {
    const response = await axiosInstance.post<{
      plan_code: string | null;
      seats: number;
      members: number;
      over_seats: boolean;
    }>(
      "/action/account/billing/attach",
      { subscription_id: subscriptionId },
      { params: orgId ? { org_id: orgId } : undefined },
    );
    return response.data;
  } catch (error) {
    log.error("attachSubscription failed", error);
    return null;
  }
}

/// A billing act's answer when Paddle says the subscription already ended; the
/// server has moved the mirror and the period, so billing reads are stale.
export interface SubscriptionEnded {
  ended: true;
}

export const SUBSCRIPTION_ENDED = "This subscription has already ended.";

const ENDED: SubscriptionEnded = { ended: true };

export function isEnded(result: unknown): result is SubscriptionEnded {
  return typeof result === "object" && result !== null && "ended" in result;
}

/// The door's 409 `ended`, the one refusal a caller re-reads on.
const endedRefusal = (error: unknown) =>
  isAxiosError<{ reason?: unknown } | undefined>(error) &&
  error.response?.status === 409 &&
  error.response.data?.reason === "ended";

/// Answers the resulting `scheduled_change` so the panel renders before the
/// webhook.
async function postBillingAction(
  path: "cancel" | "resume",
  orgId?: string,
): Promise<
  { scheduled_change: ScheduledChangeView | null } | SubscriptionEnded | null
> {
  try {
    const response = await axiosInstance.post<{
      scheduled_change: ScheduledChangeView | null;
    }>(`/action/account/billing/${path}`, null, {
      params: orgId ? { org_id: orgId } : undefined,
    });
    return response.data;
  } catch (error) {
    if (endedRefusal(error)) return ENDED;
    log.error(`billing ${path} failed`, error);
    return null;
  }
}

export const cancelSubscription = (orgId?: string) =>
  postBillingAction("cancel", orgId);

export const resumeSubscription = (orgId?: string) =>
  postBillingAction("resume", orgId);

/// Sets the subscription to `seats`, the whole count. An increase is live at
/// once; a reduction answers the seats still live and `pending_from`.
export async function addSeats(
  orgId: string | undefined,
  seats: number,
): Promise<
  { seats: number; pending_from?: number } | SubscriptionEnded | null
> {
  try {
    const response = await axiosInstance.post<{
      seats: number;
      pending_from?: number;
    }>(
      "/action/account/billing/seats",
      { seats },
      { params: orgId ? { org_id: orgId } : undefined },
    );
    return response.data;
  } catch (error) {
    if (endedRefusal(error)) return ENDED;
    log.error("addSeats failed", error);
    return null;
  }
}

/// Paddle's figures for a seat change, tax included, in the subscription's
/// currency and lowest unit.
export interface SeatsPreview {
  currency_code: string;
  /// Charged as the seats are added: a yearly subscription.
  charged_now: number | null;
  /// The prorated difference the next invoice carries: a monthly subscription.
  next_invoice_adds: number | null;
  recurring: number;
}

/// `null` when Paddle could not price the change.
export async function previewSeats(
  orgId: string | undefined,
  seats: number,
  signal?: AbortSignal,
): Promise<SeatsPreview | SubscriptionEnded | null> {
  try {
    const response = await axiosInstance.post<SeatsPreview>(
      "/action/account/billing/seats/preview",
      { seats },
      { params: orgId ? { org_id: orgId } : undefined, signal },
    );
    return response.data;
  } catch (error) {
    if (endedRefusal(error)) return ENDED;
    if (!signal?.aborted) log.warning("previewSeats failed", error);
    return null;
  }
}

/// `total` is the lowest currency unit as a string — format with
/// `formatProcessorMoney`, never `Number(total)/100`.
export interface InvoiceView {
  transaction_id: string;
  /// `null` until Paddle completes the transaction.
  invoice_number: string | null;
  status: string;
  /// Unix seconds; `null` for a transaction not yet billed.
  billed_at: number | null;
  total: string;
  currency_code: string;
  /// Refunds and credits on this charge, summed, in `total`'s unit. `null`
  /// covers both "none" and "could not ask", so render it only when present.
  refunded: string | null;
}

interface InvoicesResponse {
  invoices: InvoiceView[];
  has_more: boolean;
  next_cursor: string | null;
}

/// `null` on failure, distinct from an empty page.
export async function fetchInvoices(
  orgId?: string,
  after?: string,
): Promise<InvoicesResponse | null> {
  try {
    const response = await axiosInstance.get<InvoicesResponse>(
      "/action/account/billing/invoices",
      {
        params: {
          ...(orgId ? { org_id: orgId } : {}),
          ...(after ? { after } : {}),
        },
      },
    );
    return response.data;
  } catch (error) {
    log.error("fetchInvoices failed", error);
    return null;
  }
}

/// The signed URL is short-lived: fetch it on the click.
export async function fetchInvoicePdfUrl(
  transactionId: string,
  orgId?: string,
): Promise<string | null> {
  try {
    const response = await axiosInstance.get<{ url: string }>(
      `/action/account/billing/invoices/${encodeURIComponent(transactionId)}/pdf`,
      { params: orgId ? { org_id: orgId } : undefined },
    );
    return response.data.url;
  } catch (error) {
    log.error("fetchInvoicePdfUrl failed", error);
    return null;
  }
}

/// Single-use and expiring, never stored.
interface PortalSession {
  url: string;
}

export async function fetchBillingContext(
  orgId?: string,
): Promise<BillingContext | null> {
  try {
    const response = await axiosInstance.get<BillingContext>(
      "/action/account/billing/context",
      { params: orgId ? { org_id: orgId } : undefined },
    );
    return response.data;
  } catch (error) {
    log.error("fetchBillingContext failed", error);
    return null;
  }
}

/// `country_code` decides the tax.
export interface BillingAddress {
  first_line: string | null;
  second_line: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country_code: string;
}

interface BusinessContact {
  name: string;
  email: string;
}

export interface BillingBusiness {
  name: string;
  company_number: string | null;
  tax_identifier: string | null;
  contacts: BusinessContact[];
}

/// Read live off the subscription, never mirrored.
export interface BillingDetails {
  /// `false` when there is nothing live to invoice.
  editable: boolean;
  name: string | null;
  address: BillingAddress | null;
  business: BillingBusiness | null;
}

/// Only what is sent is written; an absent part is left as it is.
export interface BillingDetailsUpdate {
  name?: string;
  address?: BillingAddress;
  business?: BillingBusiness;
}

export function fetchBillingDetails(
  orgId: string | undefined,
): Promise<BillingDetails> {
  return doorCall("Billing details could not be read.", async () => {
    const response = await axiosInstance.get<BillingDetails>(
      "/action/account/billing/details",
      { params: orgId ? { org_id: orgId } : undefined },
    );
    return response.data;
  });
}

export function saveBillingDetails(
  orgId: string | undefined,
  update: BillingDetailsUpdate,
): Promise<void> {
  return doorCall("Billing details could not be saved.", async () => {
    await axiosInstance.put("/action/account/billing/details", update, {
      params: orgId ? { org_id: orgId } : undefined,
    });
  });
}

interface BillingContactResult {
  billed_to: string;
}

/// What saving a billing address would do. `taken`: another billing account
/// holds it and the door will refuse. `free`: the org has no account yet.
export type ContactPreviewOutcome =
  "same" | "free" | "rename" | "taken" | "unknown";

export async function previewBillingContact(
  orgId: string | undefined,
  email: string,
): Promise<ContactPreviewOutcome | null> {
  try {
    // POST with the address in the body: a query string lands in access logs.
    const response = await axiosInstance.post<{
      outcome: ContactPreviewOutcome;
    }>(
      "/action/account/billing/contact/preview",
      { email },
      { params: orgId ? { org_id: orgId } : undefined },
    );
    return response.data.outcome;
  } catch {
    return null;
  }
}

export function setBillingEmail(
  orgId: string | undefined,
  email: string,
): Promise<BillingContactResult> {
  return doorCall("The billing address could not be changed.", async () => {
    const response = await axiosInstance.put<BillingContactResult>(
      "/action/account/billing/contact",
      { email },
      { params: orgId ? { org_id: orgId } : undefined },
    );
    return response.data;
  });
}

export async function mintCardTransaction(
  orgId: string | undefined,
): Promise<string | SubscriptionEnded | null> {
  try {
    const response = await axiosInstance.post<{ transaction_id: string }>(
      "/action/account/billing/card-transaction",
      null,
      { params: orgId ? { org_id: orgId } : undefined },
    );
    return response.data.transaction_id;
  } catch (error) {
    if (endedRefusal(error)) return ENDED;
    log.error("card transaction could not be minted", error);
    return null;
  }
}

/// The URL expires, so call on the click.
export async function createPortalSession(
  orgId?: string,
): Promise<PortalSession | null> {
  try {
    const response = await axiosInstance.post<PortalSession>(
      "/action/account/billing/portal-session",
      null,
      { params: orgId ? { org_id: orgId } : undefined },
    );
    return response.data;
  } catch (error) {
    // 409: the caller is not the payer. The link is already hidden for them.
    if (!(isAxiosError(error) && error.response?.status === 409)) {
      log.error("createPortalSession failed", error);
    }
    return null;
  }
}

/// The active plan catalog, in sort order. Public and unauthenticated.
export async function fetchPlanCatalog(): Promise<CatalogPlan[]> {
  try {
    const response =
      await axiosInstance.get<CatalogPlan[]>("/api/public/plans");
    return response.data;
  } catch (error) {
    log.error("fetchPlanCatalog failed", error);
    return [];
  }
}

export async function fetchOrgs(): Promise<Org[]> {
  try {
    const response = await axiosInstance.get<Org[]>("/action/organizations");
    return response.data;
  } catch (error) {
    log.error("fetchOrgs failed", error);
    return [];
  }
}

/// The org's space tree, narrowed server-side to what the caller can see
/// (org governors get the whole tree as metadata, D3/D13).
export async function fetchOrgSpaces(orgId: string): Promise<Space[]> {
  try {
    const response = await axiosInstance.get<Space[]>(
      `/action/organizations/${orgId}/spaces`,
    );
    return response.data;
  } catch (error) {
    log.error("fetchOrgSpaces failed", error);
    return [];
  }
}

export async function createSpace(
  orgId: string,
  name: string,
  options: {
    parentSpaceId?: string;
    color?: string;
    sealed?: boolean;
    /// `open` | `restricted` | `sealed` (D39); wins over `sealed` when set.
    classification?: string;
    /// client_ids; required non-empty when sealed (D11). Every named manager
    /// must be an org member (D26).
    managers?: string[];
  } = {},
): Promise<Space | null> {
  try {
    const response = await axiosInstance.post<Space>(`/action/spaces`, {
      org_id: orgId,
      name,
      ...(options.parentSpaceId
        ? { parent_space_id: options.parentSpaceId }
        : {}),
      ...(options.color ? { color: options.color } : {}),
      ...(options.sealed ? { sealed: true } : {}),
      ...(options.classification
        ? { classification: options.classification }
        : {}),
      ...(options.managers?.length ? { managers: options.managers } : {}),
    });
    return response.data;
  } catch (error) {
    log.error("createSpace failed", error);
    // The door 400s/403s with a human-readable reason (palette name, org
    // membership, …) — surface it instead of a generic failure.
    if (isAxiosError(error) && typeof error.response?.data === "string") {
      throw new Error(error.response.data);
    }
    return null;
  }
}

export async function fetchOrgMembers(orgId: string): Promise<OrgMember[]> {
  try {
    const response = await axiosInstance.get<OrgMember[]>(
      `/action/organizations/${orgId}/members`,
    );
    return response.data;
  } catch (error) {
    log.error("fetchOrgMembers failed", error);
    return [];
  }
}

/// Who holds every space in the org. Governor-gated, and the only read that
/// answers for a space the caller was never granted — `fetchSpaceGrants` is
/// viewer-gated and 403s a governor on someone else's space.
export interface OrgSpaceAccess {
  space_id: string;
  /// No manager reaches it. With D1 making the manager set the owner set of
  /// every model in a space, that is content owned by nobody.
  unmanaged: boolean;
  holders: SpaceGrant[];
}

export async function fetchOrgSpaceAccess(
  orgId: string,
): Promise<OrgSpaceAccess[]> {
  try {
    const response = await axiosInstance.get<OrgSpaceAccess[]>(
      `/action/organizations/${orgId}/spaces/access`,
    );
    return response.data;
  } catch (error) {
    log.error("fetchOrgSpaceAccess failed", error);
    return [];
  }
}

export async function fetchSpaceGrants(spaceId: string): Promise<SpaceGrant[]> {
  const result = await fetchSpaceGrantsResult(spaceId);
  return result.status === "ok" ? result.grants : [];
}

export type SpaceGrantsResult =
  | { status: "ok"; grants: SpaceGrant[] }
  | { status: "refused" }
  | { status: "failed" };

/// `fetchSpaceGrants` with the outcome kept: a 403 is an answer (not in the
/// space, or no such space); anything else is a fault to retry.
export async function fetchSpaceGrantsResult(
  spaceId: string,
): Promise<SpaceGrantsResult> {
  try {
    const response = await axiosInstance.get<SpaceGrant[]>(
      `/action/spaces/${spaceId}/grants`,
    );
    return { status: "ok", grants: response.data };
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    if (status === 401 || status === 403) {
      return { status: "refused" };
    }
    log.error("fetchSpaceGrantsResult failed", error);
    return { status: "failed" };
  }
}

/// Who the reassign control suggests: grant-holders on the space or an ancestor,
/// narrowed to org members. `null` on failure, never `[]` — an empty list is a
/// real answer the dialog words as one, so unknown must stay distinct from zero.
export async function fetchManagerCandidates(
  spaceId: string,
): Promise<ManagerCandidate[] | null> {
  try {
    const response = await axiosInstance.get<ManagerCandidate[]>(
      `/action/spaces/${spaceId}/manager_candidates`,
    );
    return response.data;
  } catch (error) {
    log.error("fetchManagerCandidates failed", error);
    return null;
  }
}

/// Grant by client_id (org members, from the roster) or by email (guests —
/// the caller has no id for someone outside the org). Throws on refusal so
/// the dialog can show the door's message (guest ceiling, closed non-personal org).
/// `replaces` makes it a role change: the door swaps the two roles in one act,
/// so a failure leaves the old one.
export async function grantSpaceRole(
  spaceId: string,
  target: { user?: string; email?: string },
  role: string,
  replaces?: string,
): Promise<void> {
  await axiosInstance.post(`/action/spaces/grant`, {
    space_id: spaceId,
    ...target,
    role,
    ...(replaces ? { replaces } : {}),
  });
}

export async function revokeSpaceRole(
  spaceId: string,
  user: string,
  role: string,
): Promise<void> {
  await axiosInstance.post(`/action/spaces/revoke`, {
    space_id: spaceId,
    user,
    role,
  });
}

/// Name and/or color; the endpoint keeps its historical `/rename` path.
export async function updateSpace(
  spaceId: string,
  patch: { name?: string; color?: string },
): Promise<Space | null> {
  try {
    const response = await axiosInstance.post<Space>(`/action/spaces/rename`, {
      space_id: spaceId,
      ...patch,
    });
    return response.data;
  } catch (error) {
    log.error("updateSpace failed", error);
    return null;
  }
}

/// Mirrors the server `SpaceDeletionScheduledResponse`.
export interface SpaceDeletionScheduled {
  scheduled_at: string;
  purge_at: string;
  /// Every row that entered the window in this act — the space first, then
  /// its doomed subtree (D53).
  spaces_scheduled: string[];
  models_trashed: number;
}

/// A descendant that goes with the space (D53).
export interface SpaceDeletionChild {
  space_id: string;
  name: string;
}

/// Mirrors the server `SpaceDeletionAnalysis` — the pre-flight the delete
/// dialog renders, and the token the delete needs.
export interface SpaceDeletionAnalysis {
  space_id: string;
  name: string;
  classification: string;
  parent_space_id: string | null;
  models: OrgModelInfo[];
  total_tables: number;
  /// Tables a model outside this space imports — they lose their source.
  critical_tables: number;
  children: SpaceDeletionChild[];
  /// Direct grants on the space other than the caller's own.
  people_losing_access: number;
  confirm_token: string;
}

export async function getSpaceDeletionAnalysis(
  spaceId: string,
): Promise<SpaceDeletionAnalysis> {
  try {
    const { data } = await axiosInstance.get<SpaceDeletionAnalysis>(
      `/action/spaces/${spaceId}/deletion-analysis`,
    );
    return data;
  } catch (error) {
    throw doorError(error, "Could not analyse the space");
  }
}

/// Opens the deletion window for this space and its whole subtree (D53); nothing
/// is destroyed yet. Needs the token from `getSpaceDeletionAnalysis` (409 if stale).
export async function deleteSpace(
  spaceId: string,
  confirmToken: string,
): Promise<SpaceDeletionScheduled> {
  const { data } = await axiosInstance.post<SpaceDeletionScheduled>(
    `/action/spaces/delete`,
    { space_id: spaceId, confirm_token: confirmToken },
  );
  return data;
}

/// D28: seal an EMPTY space. Throws on refusal — the door's message carries
/// the model/child counts when the space is not empty.
export async function sealSpace(spaceId: string): Promise<Space> {
  const response = await axiosInstance.post<Space>(`/action/spaces/seal`, {
    space_id: spaceId,
  });
  return response.data;
}

/// D28: un-seal — org admins only (declassification is governance). Removes
/// the wall; inherited access is NOT restored.
export async function unsealSpace(spaceId: string): Promise<Space> {
  const response = await axiosInstance.post<Space>(`/action/spaces/unseal`, {
    space_id: spaceId,
  });
  return response.data;
}

export class DoorError extends Error {
  readonly field: string | null;
  constructor(message: string, field: string | null = null) {
    super(message);
    this.name = "DoorError";
    this.field = field;
  }
}

/// A string body is the reason; a structured one carries it under `message`
/// (`error` is a code for the caller, never rendered).
function doorError(error: unknown, fallback: string): DoorError {
  if (!isAxiosError(error)) return new DoorError(fallback);
  const body: unknown = error.response?.data;
  if (typeof body === "string") return new DoorError(body);
  if (body !== null && typeof body === "object") {
    const row = body as { message?: unknown; field?: unknown };
    const field = typeof row.field === "string" ? row.field : null;
    const prose = row.message;
    if (typeof prose === "string" && prose.length > 0) {
      return new DoorError(prose, field);
    }
  }
  return new DoorError(fallback);
}

async function doorCall<T>(
  fallback: string,
  call: () => Promise<T>,
): Promise<T> {
  try {
    return await call();
  } catch (error) {
    throw doorError(error, fallback);
  }
}

export class SeatLimitError extends Error {
  constructor(
    readonly used: number,
    readonly limit: number,
  ) {
    super("Every seat is taken.");
    this.name = "SeatLimitError";
  }
}

function seatLimitFrom(error: unknown): SeatLimitError | null {
  if (!isAxiosError(error) || error.response?.status !== 403) return null;
  const body = error.response?.data as
    { error?: unknown; used?: unknown; limit?: unknown } | undefined;
  if (
    body?.error !== "seat_limit" ||
    typeof body.used !== "number" ||
    typeof body.limit !== "number"
  ) {
    return null;
  }
  return new SeatLimitError(body.used, body.limit);
}

/// `first_space_name` NAMES the starter space `create_org` mints (D25), never
/// adds a second. No plan parameter: every org opens on Free.
export async function createOrg(payload: {
  name: string;
  first_space_name?: string;
}): Promise<Org> {
  try {
    const response = await axiosInstance.post<Org>(
      "/action/organizations",
      payload,
    );
    return response.data;
  } catch (error) {
    throw doorError(error, "Could not create the organization");
  }
}

/// One line of the post-create checklist. `key` is stable; `label` is server
/// copy so the two can't drift.
export interface ReadinessCheck {
  key: string;
  label: string;
  ok: boolean;
  detail?: string;
}

export interface OrgReadiness {
  ready: boolean;
  checks: ReadinessCheck[];
}

/// Re-reads every fact a finished org needs through the doors enforcement uses:
/// two writes in `create_org` are best-effort, so a 200 is not a whole org.
export async function fetchOrgReadiness(orgId: string): Promise<OrgReadiness> {
  const response = await axiosInstance.get<OrgReadiness>(
    `/action/organizations/${orgId}/readiness`,
  );
  return response.data;
}

/// The caller's own headroom, plus the offer a new org would open on. Shared with
/// the create door, so a disabled button and a 403 cannot disagree.
export interface OrgLimits {
  owned_free_orgs: number;
  max_free_orgs: number;
  can_create_free_org: boolean;
  default_plan?: { name: string; limits: PlanLimits };
}

export async function fetchOrgLimits(): Promise<OrgLimits> {
  const response = await axiosInstance.get<OrgLimits>(
    "/action/organizations/limits",
  );
  return response.data;
}

/// The create flow's rollback when a readiness line never lands: a
/// half-provisioned org must not stay alive. The door re-runs the checks and
/// REFUSES a complete org, so this cannot be used as a delete.
export async function abandonIncompleteOrg(orgId: string): Promise<void> {
  try {
    await axiosInstance.post(`/action/organizations/${orgId}/abandon`);
  } catch (error) {
    throw doorError(error, "Could not roll back the organization");
  }
}

/// Returns the updated org. Throws the door's message on refusal.
export async function renameOrg(orgId: string, name: string): Promise<Org> {
  try {
    const response = await axiosInstance.post<Org>(
      "/action/organizations/rename",
      { org_id: orgId, name },
    );
    return response.data;
  } catch (error) {
    throw doorError(error, "Rename failed");
  }
}

export type InviteStatus = "invited" | "invited_new_user" | "already_member";

/// Membership is consent-gated (D43): both outcomes are INVITATIONS. A known
/// address is told in-app; an unknown one gets a registration email. Nobody
/// joins until they accept.
export async function addOrgMember(
  orgId: string,
  email: string,
): Promise<InviteStatus> {
  try {
    const response = await axiosInstance.post<{ status: InviteStatus }>(
      "/action/organizations/members",
      { org_id: orgId, email },
    );
    return response.data.status;
  } catch (error) {
    throw seatLimitFrom(error) ?? doorError(error, "Invite failed");
  }
}

/// A pending invitation as the INVITEE sees it — the overview banner's row.
export interface MyOrgInvitation {
  org_id: string;
  org_name: string;
  inviter_name: string;
  created_at: string;
}

export async function fetchMyOrgInvitations(): Promise<MyOrgInvitation[]> {
  try {
    const response = await axiosInstance.get<MyOrgInvitation[]>(
      "/action/organizations/invitations",
    );
    return response.data;
  } catch (error) {
    log.error("fetchMyOrgInvitations failed", error);
    return [];
  }
}

export async function respondToOrgInvitation(
  orgId: string,
  accept: boolean,
): Promise<void> {
  try {
    await axiosInstance.post("/action/organizations/invitations/respond", {
      org_id: orgId,
      accept,
    });
  } catch (error) {
    throw doorError(
      error,
      accept ? "Could not join the organization" : "Could not decline",
    );
  }
}

/// What leaving an org would do — feeds the confirm dialog.
export interface LeaveOrgPreview {
  is_owner_blocked: boolean;
  /// Non-empty spaces the member solely manages. Leaving strands them for a
  /// governor to reassign — a warning, not a blocker (D49).
  spaces_to_orphan: string[];
  spaces_to_delete: string[];
  grants_to_revoke: string[];
  /// The org's subscription is on this member's card; leaving does not move it.
  pays_for_org: boolean;
}

/// What removing this member would do (D43) — the admin's half of the same
/// analysis the leave door runs.
export async function fetchRemoveMemberPreview(
  orgId: string,
  clientId: string,
): Promise<LeaveOrgPreview> {
  try {
    const response = await axiosInstance.get<LeaveOrgPreview>(
      `/action/organizations/${orgId}/members/${clientId}/removal_preview`,
    );
    return response.data;
  } catch (error) {
    throw doorError(error, "Could not check what removing would do");
  }
}

export async function fetchLeaveOrgPreview(
  orgId: string,
): Promise<LeaveOrgPreview> {
  try {
    const response = await axiosInstance.get<LeaveOrgPreview>(
      `/action/organizations/${orgId}/leave_preview`,
    );
    return response.data;
  } catch (error) {
    throw doorError(error, "Could not check what leaving would do");
  }
}

export async function leaveOrg(orgId: string): Promise<void> {
  try {
    await axiosInstance.post("/action/organizations/leave", {
      org_id: orgId,
    });
  } catch (error) {
    throw doorError(error, "Could not leave the organization");
  }
}

/// `onlyIfPending` is a precondition for the Cancel-invitation affordance: if
/// they accepted while the roster sat open, the door refuses rather than
/// quietly removing a colleague the caller never meant to touch.
export async function removeOrgMember(
  orgId: string,
  clientId: string,
  onlyIfPending = false,
): Promise<void> {
  try {
    await axiosInstance.post("/action/organizations/members/remove", {
      org_id: orgId,
      client_id: clientId,
      only_if_pending: onlyIfPending,
    });
  } catch (error) {
    throw doorError(error, "Removal failed");
  }
}

/// Grant or revoke a co-owner. An org may hold any number of owners; the
/// server refuses to remove the last one.
export async function setOrgOwner(
  orgId: string,
  clientId: string,
  isOwner: boolean,
): Promise<void> {
  try {
    await axiosInstance.put("/action/organizations/owner", {
      org_id: orgId,
      client_id: clientId,
      is_owner: isOwner,
    });
  } catch (error) {
    throw doorError(error, "Role change failed");
  }
}

export async function setOrgAdmin(
  orgId: string,
  clientId: string,
  isAdmin: boolean,
): Promise<void> {
  try {
    await axiosInstance.put("/action/organizations/admin", {
      org_id: orgId,
      client_id: clientId,
      is_admin: isAdmin,
    });
  } catch (error) {
    throw doorError(error, "Role change failed");
  }
}

/// Opens the deletion grace window — nothing is destroyed yet. The cascade
/// runs after `purge_at`; until then any admin can call `cancelOrgDeletion`.
export async function deleteOrg(orgId: string): Promise<OrgDeletionScheduled> {
  try {
    const { data } = await axiosInstance.post<OrgDeletionScheduled>(
      "/action/organizations/delete",
      { org_id: orgId },
    );
    return data;
  } catch (error) {
    throw doorError(error, "Delete failed");
  }
}

/// One of the org's models and the tables that go with it. `usage_status`
/// ranks each table's blast radius: Critical means a model in ANOTHER org
/// imports it, so someone outside loses data.
export interface OrgModelInfo {
  model_id: string;
  model_name: string;
  tables: {
    table_id: string;
    table_name: string;
    usage_status: "Critical" | "Moderate" | "Low";
    external_link_count?: number;
  }[];
}

/// Someone whose work dies with the org, most work first.
export interface OrgContributor {
  client_id: string;
  display_name: string;
  model_count: number;
  table_count: number;
}

export interface OrgDeletionAnalysis {
  org_id: string;
  org_name: string;
  member_count: number;
  spaces: { space_id: string; name: string; sealed: boolean }[];
  /// Non-empty means the org can be handed over instead of destroyed.
  models: OrgModelInfo[];
  total_tables: number;
  /// Tables imported by a model in another org — the ones someone outside
  /// loses when the window closes.
  critical_tables: number;
  contributors: OrgContributor[];
}

export async function getOrgDeletionAnalysis(
  orgId: string,
): Promise<OrgDeletionAnalysis> {
  try {
    const { data } = await axiosInstance.get<OrgDeletionAnalysis>(
      `/action/organizations/${orgId}/deletion-analysis`,
    );
    return data;
  } catch (error) {
    throw doorError(error, "Could not analyse the organization");
  }
}

/// D53: a doomed subtree as the Trash shows it — one header per doomed ROOT,
/// for every manager of it, present even when it holds no models.
export interface TrashedSpace {
  space_id: string;
  org_id: string;
  name: string;
  color: string;
  classification: string;
  deletion_scheduled_at: string;
  purge_at: string;
  /// False once the teardown has claimed it, or while the whole org is
  /// scheduled (then only the org's cancel applies).
  cancellable: boolean;
  deleted_by: string | null;
  /// The doomed descendants that come back with it.
  sub_spaces: SpaceDeletionChild[];
  /// D54: the org this space goes with, when the whole org is scheduled.
  with_org: string | null;
  /// D55: that org is a personal one — it goes with its account, and the
  /// account settings own the cancel.
  with_account: boolean;
}

/// D41: org-scoped, and the door refuses without a context (400).
export async function fetchTrashedSpaces(
  orgId: string,
): Promise<TrashedSpace[]> {
  const response = await axiosInstance.get<TrashedSpace[]>(
    "/action/spaces/trash",
    { params: { org_id: orgId } },
  );
  return Array.isArray(response.data) ? response.data : [];
}

export interface SpaceRestored {
  spaces_restored: string[];
  models_restored: number;
}

/// D53 "Restore space": the doomed subtree under this root comes back, every
/// model in it with it. Only a root has this door.
export async function restoreSpace(spaceId: string): Promise<SpaceRestored> {
  try {
    const response = await axiosInstance.post<Partial<SpaceRestored>>(
      "/action/spaces/cancel_deletion",
      { space_id: spaceId },
    );
    return {
      spaces_restored: response.data?.spaces_restored ?? [spaceId],
      models_restored: response.data?.models_restored ?? 0,
    };
  } catch (error) {
    throw doorError(error, "Could not restore the space");
  }
}

export async function cancelSpaceDeletion(spaceId: string): Promise<void> {
  try {
    await axiosInstance.post("/action/spaces/cancel_deletion", {
      space_id: spaceId,
    });
  } catch (error) {
    throw doorError(error, "Could not cancel the deletion");
  }
}

export async function cancelOrgDeletion(orgId: string): Promise<void> {
  try {
    await axiosInstance.post("/action/organizations/cancel_deletion", {
      org_id: orgId,
    });
  } catch (error) {
    throw doorError(error, "Could not cancel the deletion");
  }
}

/// `dataUrl` comes from `resizeImage` — already downscaled and rasterised.
export async function updateOrgImage(
  orgId: string,
  dataUrl: string,
): Promise<void> {
  try {
    await axiosInstance.put(`/action/organizations/${orgId}/image`, {
      image: dataUrl,
    });
  } catch (error) {
    throw doorError(error, "Image upload failed");
  }
}

export async function removeOrgImage(orgId: string): Promise<void> {
  try {
    await axiosInstance.delete(`/action/organizations/${orgId}/image`);
  } catch (error) {
    throw doorError(error, "Image removal failed");
  }
}

/// The membership-gated serving URL for an org's image. `version` busts the
/// browser cache after an upload, like the avatar's `?v=`.
export function orgImageUrl(orgId: string, version: number): string {
  return `/server/action/organizations/${orgId}/image?v=${version}`;
}

export interface GovernanceAuditPage {
  entries: GovernanceAuditEntry[];
  /// Pass back as `before` to page further. `null` means this was the last page.
  next_cursor: number | null;
}

export interface GovernanceAuditQuery {
  /// Narrow to one space. Omitted means the whole org's log, org-level acts
  /// included.
  spaceId?: string;
  before?: number;
  limit?: number;
  /// Read through the platform admin's door, which names MyCorr admins and
  /// their reasons instead of "MyCorr Admin".
  admin?: boolean;
}

const auditBase = (orgId: string, admin = false): string =>
  admin
    ? `/action/admin/organizations/${orgId}`
    : `/action/organizations/${orgId}`;

/// Throws on failure. Use for Load more, where an error must read as an error
/// rather than as "the log just ran out".
export async function fetchGovernanceAuditPage(
  orgId: string,
  query: GovernanceAuditQuery = {},
): Promise<GovernanceAuditPage> {
  const response = await axiosInstance.get<GovernanceAuditPage>(
    `${auditBase(orgId, query.admin)}/audit`,
    {
      params: {
        space_id: query.spaceId,
        before: query.before,
        limit: query.limit,
      },
    },
  );
  return response.data;
}

export interface AuditNames {
  models: Record<string, string>;
  spaces: Record<string, string>;
}

/// Names for the ids a page of the log holds: live, else the last one the log
/// recorded. An id without one, or a failed call, is absent.
export async function fetchAuditNames(
  orgId: string,
  ids: { models: string[]; spaces: string[] },
  admin = false,
): Promise<AuditNames> {
  if (ids.models.length === 0 && ids.spaces.length === 0) {
    return { models: {}, spaces: {} };
  }
  try {
    const response = await axiosInstance.get<AuditNames>(
      `${auditBase(orgId, admin)}/audit/names`,
      {
        params: { models: ids.models.join(","), spaces: ids.spaces.join(",") },
      },
    );
    return response.data;
  } catch {
    return { models: {}, spaces: {} };
  }
}

/// One export of table data (docs/ExportAudit.md). The actor is named at read
/// time; table and model names were copied when the data left.
export interface ExportAuditEntry {
  id: string;
  created_at: string;
  org_id: string;
  space_id: string | null;
  space_name: string | null;
  model_id: string | null;
  model_name: string | null;
  table_id: string;
  table_name: string;
  table_version: number;
  actor_id: string;
  actor_name: string | null;
  actor_email: string | null;
  credential: "session" | "token" | "mcp";
  token_id: string | null;
  door: "table" | "view" | "stream" | "sheets" | "attachment";
  /// The client's own label: `csv`, `parquet`, `clipboard`. Never trusted.
  purpose: string | null;
  outcome: "completed" | "incomplete";
  byte_count: number;
  row_count: number | null;
  column_ids: string[];
  ip: string | null;
  user_agent: string | null;
  detail: Record<string, unknown>;
}

export interface ExportAuditPage {
  /// `false` when the org's plan does not include the log.
  available: boolean;
  entries: ExportAuditEntry[];
  next_cursor: string | null;
}

/// Throws on failure, including the 403 a non-governor gets.
export async function fetchExportAuditPage(
  orgId: string,
  query: { before?: string; limit?: number; admin?: boolean } = {},
): Promise<ExportAuditPage> {
  const response = await axiosInstance.get<ExportAuditPage>(
    `${auditBase(orgId, query.admin)}/exports`,
    { params: { before: query.before, limit: query.limit } },
  );
  return response.data;
}

/// 403 (not an org admin) resolves to null — the first load doubles as the
/// permission probe, and the settings page hides the governance section rather
/// than erroring.
export async function fetchGovernanceAudit(
  orgId: string,
  query: GovernanceAuditQuery = {},
): Promise<GovernanceAuditPage | null> {
  try {
    return await fetchGovernanceAuditPage(orgId, query);
  } catch {
    return null;
  }
}
